import type { CeremonyPreview, CeremonyClient } from '@meridian/release-ceremony';

// The Ops-side ceremony client: the same mission endpoints, reached through
// the daemon's proxy on this origin. The operator session cookie (re-homed
// by the proxy) rides automatically; a 401 means "sign in as the Console
// owner", which the host renders as the sheet's first step.

export class MissionError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/mission${path}`, {
    method,
    headers: {
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(method !== 'GET' ? { 'x-ops-app': '1' } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: 'same-origin',
  });
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = null;
  }
  if (!res.ok) {
    const msg =
      (parsed as { message?: string | string[]; error?: string })?.message ??
      (parsed as { error?: string })?.error ??
      `Request failed (${res.status}).`;
    throw new MissionError(res.status, Array.isArray(msg) ? msg.join(', ') : String(msg));
  }
  return parsed as T;
}

export interface OperatorMe {
  id: string;
  email: string;
  displayName: string;
  role: 'owner' | 'operator' | 'viewer';
}

export interface MissionCandidate {
  id: string;
  version: string;
  gitSha: string;
  state: string;
  ciStatus?: string | null;
}

export const missionOps = {
  me: () => call<OperatorMe>('GET', '/auth/me'),
  login: (email: string, password: string) =>
    call<
      | { status: 'authenticated'; displayName: string; role: string }
      | { challengeToken: string; method?: string; hint?: string }
    >('POST', '/auth/login', { email, password }),
  mfaVerify: (challengeToken: string, code: string) =>
    call<{ status: 'authenticated'; displayName: string; role: string }>('POST', '/auth/mfa/verify', {
      challengeToken,
      code,
    }),
  logout: () => call<{ ok: boolean }>('POST', '/auth/logout'),
  candidates: (limit = 30) =>
    call<{ candidates: MissionCandidate[]; nextCursor: string | null }>('GET', `/releases?limit=${limit}`),
  promotionPreview: (candidateId: string) =>
    call<CeremonyPreview>('GET', `/releases/${candidateId}/promotion-preview`),
  promotionChallenge: ((candidateId: string) =>
    call('POST', `/releases/${candidateId}/promotion-challenge`)) as CeremonyClient['promotionChallenge'],
  promoteRelease: ((candidateId, body) =>
    call('POST', `/releases/${candidateId}/promote`, body)) as CeremonyClient['promoteRelease'],
  rollout: (rolloutId: string) => call<RolloutView>('GET', `/releases/rollouts/${rolloutId}`),
  activeRollout: () =>
    call<{ rollout: { id: string; state: string; candidate: { version: string }; hospitalsTotal: number; hospitalsMoved: number } | null }>(
      'GET',
      '/releases/rollouts/active',
    ),
};

// The mission RolloutView, trimmed to what the watch renders.
export interface RolloutView {
  id: string;
  kind: 'promote' | 'fleet_rollback';
  state: string;
  candidate: { id: string; version: string; gitSha: string };
  fromVersion: string | null;
  startedByName: string;
  pauseReason: string | null;
  completedAt: string | null;
  error: string | null;
  createdAt: string;
  waves: Array<{
    id: string;
    index: number;
    name: string;
    holdMinutes: number;
    tenants: Array<{
      tenantId: string;
      slug?: string;
      displayName?: string;
      state?: string;
      status?: string;
      error?: string | null;
    }>;
  }>;
}
